import {
  enableableButtonOverrideSchema,
  enableableInputOverrideSchema,
} from "@redotech/redo-content-builder-common/brand-kit-zod";
import { buttonHierarchies } from "@redotech/redo-content-builder-common/element-schema";
import { ImageType } from "@redotech/redo-content-builder-common/image-type";
import { Provider } from "@redotech/redo-order-common/provider";
import type { OrderLookupSettings } from "@redotech/redo-order-tracking-lookup-common/order-lookup-defaults";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { OrderTrackingShipmentStatus } from "./order-tracking-types";
import {
  defaultPrimaryButtonConfiguration,
  defaultSecondaryButtonConfiguration,
  trackingPagePageButtonSettingsDefault,
} from "./tracking-page-button-defaults";

export const gameDifficultySchema = createEnum(["easy", "medium", "hard"]);
export type GameDifficulty = z.infer<typeof gameDifficultySchema>;

export const TRACKING_PAGE_GRID_COLUMNS = 7;
export const TRACKING_PAGE_GRID_ROWS = 9;
export const TRACKING_PAGE_GRID_CELL_SIZE = 160;
export const TRACKING_PAGE_GRID_GAP = 16;

// Width of the desktop tracking page grid container, including a 20px gutter
// on each side. Below this viewport width, the page falls back to the stacked
// mobile layout instead of clipping right-side blocks.
export const TRACKING_PAGE_DESKTOP_WIDTH_PX =
  TRACKING_PAGE_GRID_COLUMNS * TRACKING_PAGE_GRID_CELL_SIZE +
  (TRACKING_PAGE_GRID_COLUMNS - 1) * TRACKING_PAGE_GRID_GAP +
  40;

// Hard ceiling for the merchant-configurable page height (`minPageHeightPx`) on
// both the tracking page and the order lookup page. Guards against runaway
// values regardless of background image dimensions, and is enforced in both the
// builder UI and the persisted schema.
export const ABSOLUTE_MAX_PAGE_HEIGHT = 8000;

/**
 * Resolves the CSS `min-height` for a page wrapper given an optional
 * merchant-configured page height (in px) and the wrapper's default height.
 *
 * Returns `fallback` unchanged when no height is configured, so existing pages
 * render identically. When a height is configured it is combined with the
 * fallback via `max()` so the page never shrinks below its default (e.g. the
 * viewport) and — because this is applied as `min-height`, never `height` —
 * content taller than the configured value is never clipped.
 */
export function pageMinHeightCss(
  minPageHeightPx: number | null | undefined,
  fallback: string,
): string {
  return minPageHeightPx && minPageHeightPx > 0
    ? `max(${fallback}, ${minPageHeightPx}px)`
    : fallback;
}

export enum TrackingPageBlockType {
  HEADER = "header",
  TRACKING_DETAILS = "tracking-details",
  ITEMS_IN_FULFILLMENT = "items-in-fulfillment",
  PRODUCT_UPSELL = "product-upsell",
  PROMOTION = "promotion",
  GAME = "game",
  SUBSCRIPTION_UPSELL = "subscription-upsell",
  TRACKING_UPDATES = "tracking-updates",
  TEXT = "text",
  OFFERS = "offers",
}

export enum OfferNetworkProvider {
  ROKT = "rokt",
  FALCON = "falcon",
}

export const prettyOfferNetworkProvider = {
  [OfferNetworkProvider.ROKT]: "Rokt",
  [OfferNetworkProvider.FALCON]: "Falcon Labs",
};

export enum SubscriptionUpsellFallbackCondition {
  CUSTOMER_IS_SUBSCRIBED = "customer-is-subscribed",
}

export enum TrackingMapPinShape {
  DEFAULT = "default",
  ROUNDED = "rounded",
}

export enum PromotionType {
  PAGE = "page",
  SUBSCRIBE = "subscribe",
}

export const DEFAULT_BLOCK_SIZES: Record<
  TrackingPageBlockType,
  { width: number; height: number }
> = {
  [TrackingPageBlockType.HEADER]: { width: 7, height: 1 },
  [TrackingPageBlockType.TRACKING_DETAILS]: { width: 2, height: 3 },
  [TrackingPageBlockType.ITEMS_IN_FULFILLMENT]: { width: 5, height: 3 },
  [TrackingPageBlockType.PRODUCT_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.PROMOTION]: { width: 2, height: 3 },
  [TrackingPageBlockType.GAME]: { width: 2, height: 3 },
  [TrackingPageBlockType.SUBSCRIPTION_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.TRACKING_UPDATES]: { width: 3, height: 1 },
  [TrackingPageBlockType.TEXT]: { width: 2, height: 1 },
  // Falcon requires a 580x260 desktop minimum; 4 columns = 688px, 2 rows = 336px
  [TrackingPageBlockType.OFFERS]: { width: 4, height: 2 },
};

export const MIN_BLOCK_SIZES: Record<
  TrackingPageBlockType,
  { width: number; height: number }
> = {
  [TrackingPageBlockType.HEADER]: { width: 1, height: 1 },
  [TrackingPageBlockType.TRACKING_DETAILS]: { width: 2, height: 2 },
  [TrackingPageBlockType.ITEMS_IN_FULFILLMENT]: { width: 2, height: 1 },
  [TrackingPageBlockType.PRODUCT_UPSELL]: { width: 2, height: 2 },
  [TrackingPageBlockType.PROMOTION]: { width: 1, height: 1 },
  [TrackingPageBlockType.GAME]: { width: 2, height: 2 },
  [TrackingPageBlockType.SUBSCRIPTION_UPSELL]: { width: 3, height: 3 },
  [TrackingPageBlockType.TRACKING_UPDATES]: { width: 3, height: 1 },
  [TrackingPageBlockType.TEXT]: { width: 1, height: 1 },
  [TrackingPageBlockType.OFFERS]: { width: 4, height: 2 },
};

export const TRACKING_DETAILS_MAP_MIN_HEIGHT = 4;

export const DEFAULT_TRACKING_MAP_HEIGHT_PX = 240;
export const MIN_TRACKING_MAP_HEIGHT_PX = 120;
export const MAX_TRACKING_MAP_HEIGHT_PX = 1000;

export const DEFAULT_TRACKING_MAP_ZOOM = 11;
export const MIN_TRACKING_MAP_ZOOM = 3;
export const MAX_TRACKING_MAP_ZOOM = 18;

export function getMinBlockSize(block: TrackingPageBlock): {
  width: number;
  height: number;
} {
  const base = MIN_BLOCK_SIZES[block.type];
  if (block.type === TrackingPageBlockType.TRACKING_DETAILS && block.showMap) {
    return {
      width: base.width,
      height: Math.max(base.height, TRACKING_DETAILS_MAP_MIN_HEIGHT),
    };
  }
  return base;
}

export enum GameType {
  GALAGA = "galaga",
  CLEANUP_CREW = "cleanup-crew",
  DELIVERY_DASH = "delivery-dash",
  FLAPPY_FREIGHT = "flappy-freight",
  TINY_PACKAGE = "tiny-package",
}

export const prettyGameType = {
  [GameType.GALAGA]: "Galaga",
  [GameType.CLEANUP_CREW]: "Cleanup Crew",
  [GameType.DELIVERY_DASH]: "Delivery Dash",
  [GameType.FLAPPY_FREIGHT]: "Flappy Freight",
  [GameType.TINY_PACKAGE]: "Tiny Package",
};

const colorSwatchesValidator = z.object({
  50: z.string(),
  100: z.string(),
  200: z.string(),
  300: z.string(),
  400: z.string(),
  500: z.string(),
  600: z.string(),
  700: z.string(),
  800: z.string(),
  900: z.string(),
  950: z.string(),
});
export interface ColorSwatches extends z.infer<typeof colorSwatchesValidator> {}

export type ColorSwatchKey = keyof z.infer<typeof colorSwatchesValidator>;

export const colorStops = Object.keys(colorSwatchesValidator.shape).map(
  Number,
) as ColorSwatchKey[];

const paletteThemeValidator = z.object({
  baseColor: z.string(),
  valueStop: z.number(),
  colorSwatches: colorSwatchesValidator,
});
export interface PaletteTheme extends z.infer<typeof paletteThemeValidator> {}

export const orderTrackingPageButtonHierarchies = [
  ...buttonHierarchies,
  "custom",
] as const;
export type OrderTrackingPageButtonHierarchy =
  (typeof orderTrackingPageButtonHierarchies)[number];

const buttonConfigurationStateSchema_ = z.object({
  style: z.object({ background: z.string(), border: z.string() }),
  text: z.object({ fontColor: z.string() }),
});
const buttonConfigurationStateSchema: z.ZodType<ButtonConfigurationState> =
  buttonConfigurationStateSchema_;
interface ButtonConfigurationState extends z.infer<
  typeof buttonConfigurationStateSchema_
> {}

const buttonConfigurationSchema_ = z.object({
  default: buttonConfigurationStateSchema,
  hovered: buttonConfigurationStateSchema
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  active: buttonConfigurationStateSchema
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});
const buttonConfigurationSchema: z.ZodType<ButtonConfiguration> =
  buttonConfigurationSchema_;
interface ButtonConfiguration extends z.infer<
  typeof buttonConfigurationSchema_
> {}

const pageButtonSettingsSchema_ = z.object({
  primary: buttonConfigurationSchema,
  secondary: buttonConfigurationSchema,
  tertiary: buttonConfigurationSchema,
  palette: paletteThemeValidator,
});
export const pageButtonSettingsSchema: z.ZodType<PageButtonSettings> =
  pageButtonSettingsSchema_;
export interface PageButtonSettings extends z.infer<
  typeof pageButtonSettingsSchema_
> {}

const buttonSettingsSchema_ = z.object({
  hierarchy: createEnum(orderTrackingPageButtonHierarchies),
  buttonConfiguration: buttonConfigurationSchema,
});
const buttonSettingsSchema: z.ZodType<ButtonSettings> = buttonSettingsSchema_;
export interface ButtonSettings extends z.infer<typeof buttonSettingsSchema_> {}

const baseTrackingPageBlockSchema_ = z.object({
  _id: zExt.objectId(),
  type: createEnum(Object.values(TrackingPageBlockType)),
  anchorCoordinates: z.object({ x: z.number(), y: z.number() }),
  /**
   * The size of the block in the grid.
   * 1 x 1, 2 x 1, 1 x 2, 2 x 2, etc.
   */
  size: z.object({ width: z.number(), height: z.number() }),
});
export const baseTrackingPageBlockSchema: z.ZodType<BaseTrackingPageBlock> =
  baseTrackingPageBlockSchema_;
export interface BaseTrackingPageBlock extends z.infer<
  typeof baseTrackingPageBlockSchema_
> {}

const trackingPageHeaderBlockSchema_ = baseTrackingPageBlockSchema_.extend({
  type: z.literal(TrackingPageBlockType.HEADER),
});
export const trackingPageHeaderBlockSchema: z.ZodType<TrackingPageHeaderBlock> =
  trackingPageHeaderBlockSchema_;
export interface TrackingPageHeaderBlock extends z.infer<
  typeof trackingPageHeaderBlockSchema_
> {}

// Non-enum tracking-details status branches merchants may also rename, kept as
// synthetic keys (no overlap with OrderTrackingShipmentStatus enum values).
export const trackingDetailsExtraStatusKeys = [
  "preparing",
  "purchasedInStore",
  "readyForPickup",
  "pickedUp",
  "cancelledMessage",
] as const;
export type TrackingDetailsExtraStatusKey =
  (typeof trackingDetailsExtraStatusKeys)[number];
export type TrackingDetailsStatusLabelKey =
  | OrderTrackingShipmentStatus
  | TrackingDetailsExtraStatusKey;

// Cap override length to prevent layout-breaking input; sized for the longest
// built-in default (the cancelled-order message, ~74 chars).
export const TRACKING_STATUS_LABEL_MAX_LENGTH = 120;

const trackingPageTrackingDetailsBlockSchema_ =
  baseTrackingPageBlockSchema_.extend({
    type: z.literal(TrackingPageBlockType.TRACKING_DETAILS),
    title: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    optionalSubtext: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    showMap: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapPinColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapPinShape: createEnum(Object.values(TrackingMapPinShape))
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapFontColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapFontFamily: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapHeightPx: z
      .number()
      .int()
      .min(MIN_TRACKING_MAP_HEIGHT_PX)
      .max(MAX_TRACKING_MAP_HEIGHT_PX)
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    mapZoom: z
      .number()
      .int()
      .min(MIN_TRACKING_MAP_ZOOM)
      .max(MAX_TRACKING_MAP_ZOOM)
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    // Presentation-only per-status label overrides keyed by the shipment-status
    // enum; absent/empty falls back to the built-in localized label.
    statusLabelOverrides: z
      .partialRecord(
        createEnum([
          ...Object.values(OrderTrackingShipmentStatus),
          ...trackingDetailsExtraStatusKeys,
        ]),
        z.string().max(TRACKING_STATUS_LABEL_MAX_LENGTH),
      )
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  });
export const trackingPageTrackingDetailsBlockSchema: z.ZodType<TrackingPageTrackingDetailsBlock> =
  trackingPageTrackingDetailsBlockSchema_;
export interface TrackingPageTrackingDetailsBlock extends z.infer<
  typeof trackingPageTrackingDetailsBlockSchema_
> {}

const trackingPageItemsInFulfillmentBlockSchema_ =
  baseTrackingPageBlockSchema_.extend({
    type: z.literal(TrackingPageBlockType.ITEMS_IN_FULFILLMENT),
    title: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    imageAspectRatio: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    hidePrice: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    hideQuantity: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    hideVirtualItems: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    hideZeroDollarItems: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    itemsInPackageTextColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    itemsInPackageBackgroundColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    itemsInPackageBorderRadius: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    buttonSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "secondary",
            buttonConfiguration: defaultSecondaryButtonConfiguration,
          },
      ),
    showReturnsDeeplink: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    returnsDeeplinkTextColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    returnsDeeplinkFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    showClaimsDeeplink: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    claimsDeeplinkTextColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    claimsDeeplinkFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  });
export const trackingPageItemsInFulfillmentBlockSchema: z.ZodType<TrackingPageItemsInFulfillmentBlock> =
  trackingPageItemsInFulfillmentBlockSchema_;
export interface TrackingPageItemsInFulfillmentBlock extends z.infer<
  typeof trackingPageItemsInFulfillmentBlockSchema_
> {}

const trackingPageProductUpsellBlockSchema_ =
  baseTrackingPageBlockSchema_.extend({
    type: z.literal(TrackingPageBlockType.PRODUCT_UPSELL),
    /**
     * Ecommerce provider that recommendations are drawn from. When unset,
     * the runtime falls back to the order's own source provider.
     */
    provider: createEnum(Provider).nullish(),
    recommendedProductFilterId: z.string().nullish(),
    /**
     * Optional secondary filter used to fill the remaining upsell slots when
     * the primary filter yields fewer products than the block displays. Tried
     * before the generic best-seller backstop.
     */
    fallbackRecommendedProductFilterId: z.string().nullish(),
    upsellText: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    buttonText: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    imageAspectRatio: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    discountId: zExt
      .objectId()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    shopFullUrl: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    checkoutButtonSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "secondary",
            buttonConfiguration: defaultSecondaryButtonConfiguration,
          },
      ),
    dropdownSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "secondary",
            buttonConfiguration: defaultSecondaryButtonConfiguration,
          },
      ),
    /**
     * Destination of the CTA button. When unset, defaults to "checkout"
     * (existing behavior: add-to-cart / checkout flow). "pdp" sends the
     * shopper to the product detail page instead.
     */
    ctaDestination: z
      .enum(["checkout", "pdp"])
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    /**
     * When true, show the variant's compareAtPrice (struck through)
     * alongside the price for recommended products.
     */
    showCompareAtPrice: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    /**
     * When true, hide the price display on upsell product cards.
     */
    hidePrice: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  });
export const trackingPageProductUpsellBlockSchema: z.ZodType<TrackingPageProductUpsellBlock> =
  trackingPageProductUpsellBlockSchema_;
export interface TrackingPageProductUpsellBlock extends z.infer<
  typeof trackingPageProductUpsellBlockSchema_
> {}

const trackingPagePromotionBlockSchema_ = baseTrackingPageBlockSchema_.extend({
  type: z.literal(TrackingPageBlockType.PROMOTION),
  imageUrl: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  url: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  altText: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  promotionType: createEnum(Object.values(PromotionType))
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  imageSourceType: createEnum(Object.values(ImageType))
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  fillBlock: z
    .boolean()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  borderColor: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});
export const trackingPagePromotionBlockSchema: z.ZodType<TrackingPagePromotionBlock> =
  trackingPagePromotionBlockSchema_;
export interface TrackingPagePromotionBlock extends z.infer<
  typeof trackingPagePromotionBlockSchema_
> {}

const trackingPageSubscriptionUpsellBlockSchema_ =
  baseTrackingPageBlockSchema_.extend({
    type: z.literal(TrackingPageBlockType.SUBSCRIPTION_UPSELL),
    fallbackCondition: createEnum(
      Object.values(SubscriptionUpsellFallbackCondition),
    )
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    fallbackBlock: createEnum(Object.values(TrackingPageBlockType))
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    // For now, we only support promotion blocks as fallback blocks
    fallbackBlockSettings: trackingPagePromotionBlockSchema_
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    title: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    descriptionOverride: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    buttonText: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    subscriptionProductId: z.string().nullish(),
    perks: z
      .array(z.string())
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    orderIntervalFrequency: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    chargeIntervalFrequency: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    isEditingFallback: z
      .boolean()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    buttonSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "primary",
            buttonConfiguration: defaultPrimaryButtonConfiguration,
          },
      ),
    secondaryButtonSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "secondary",
            buttonConfiguration: defaultSecondaryButtonConfiguration,
          },
      ),
    dropdownSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "secondary",
            buttonConfiguration: defaultSecondaryButtonConfiguration,
          },
      ),
  });
export const trackingPageSubscriptionUpsellBlockSchema: z.ZodType<TrackingPageSubscriptionUpsellBlock> =
  trackingPageSubscriptionUpsellBlockSchema_;
export interface TrackingPageSubscriptionUpsellBlock extends z.infer<
  typeof trackingPageSubscriptionUpsellBlockSchema_
> {}

const gameRewardSettingsSchema_ = z.object({
  discountId: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  showRewardOnVictory: z.boolean(),
  rewardMessage: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});
export const gameRewardSettingsSchema: z.ZodType<GameRewardSettings> =
  gameRewardSettingsSchema_;
export interface GameRewardSettings extends z.infer<
  typeof gameRewardSettingsSchema_
> {}

const trackingPageTrackingUpdatesBlockSchema_ =
  baseTrackingPageBlockSchema_.extend({
    type: z.literal(TrackingPageBlockType.TRACKING_UPDATES),
    title: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    buttonSettings: buttonSettingsSchema
      .nullish()
      .transform(
        (v): ButtonSettings =>
          v ?? {
            hierarchy: "primary",
            buttonConfiguration: defaultPrimaryButtonConfiguration,
          },
      ),
  });
export const trackingPageTrackingUpdatesBlockSchema: z.ZodType<TrackingPageTrackingUpdatesBlock> =
  trackingPageTrackingUpdatesBlockSchema_;
export interface TrackingPageTrackingUpdatesBlock extends z.infer<
  typeof trackingPageTrackingUpdatesBlockSchema_
> {}

// Base game block schema with shared properties
const baseGameBlockSchema = baseTrackingPageBlockSchema_.extend({
  type: z.literal(TrackingPageBlockType.GAME),
  gameSettings: z.object({}).nullish(),
  rewardSettings: z.object({}).nullish(),
});

// Galaga game block schema
const trackingPageGalagaGameBlockSchema_ = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.GALAGA),
  difficulty: gameDifficultySchema
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});
export const trackingPageGalagaGameBlockSchema: z.ZodType<TrackingPageGalagaGameBlock> =
  trackingPageGalagaGameBlockSchema_;
export interface TrackingPageGalagaGameBlock extends z.infer<
  typeof trackingPageGalagaGameBlockSchema_
> {}

// Delivery Dash game block schema
const trackingPageDeliveryDashGameBlockSchema_ = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.DELIVERY_DASH),
  rewardSettings: gameRewardSettingsSchema,
});
export const trackingPageDeliveryDashGameBlockSchema: z.ZodType<TrackingPageDeliveryDashGameBlock> =
  trackingPageDeliveryDashGameBlockSchema_;
export interface TrackingPageDeliveryDashGameBlock extends z.infer<
  typeof trackingPageDeliveryDashGameBlockSchema_
> {}

// Cleanup crew game block schema
const trackingPageCleanupCrewGameBlockSchema_ = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.CLEANUP_CREW),
  gameSettings: z.object({
    selectedLevel: z.number(),
    useRandomLevel: z.boolean(),
  }),
  rewardSettings: gameRewardSettingsSchema,
});
export const trackingPageCleanupCrewGameBlockSchema: z.ZodType<TrackingPageCleanupCrewGameBlock> =
  trackingPageCleanupCrewGameBlockSchema_;
export interface TrackingPageCleanupCrewGameBlock extends z.infer<
  typeof trackingPageCleanupCrewGameBlockSchema_
> {}

// Flappy freight game block schema
const trackingPageFlappyFreightGameBlockSchema_ = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.FLAPPY_FREIGHT),
  rewardSettings: gameRewardSettingsSchema,
});
export const trackingPageFlappyFreightGameBlockSchema: z.ZodType<TrackingPageFlappyFreightGameBlock> =
  trackingPageFlappyFreightGameBlockSchema_;
export interface TrackingPageFlappyFreightGameBlock extends z.infer<
  typeof trackingPageFlappyFreightGameBlockSchema_
> {}

const trackingPageTinyPackageGameBlockSchema_ = baseGameBlockSchema.extend({
  gameType: z.literal(GameType.TINY_PACKAGE),
  gameSettings: z.object({
    difficulty: gameDifficultySchema
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    playerSpriteUrl: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    playerSpriteSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    npcSpriteUrl: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    npcSpriteSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  }),
  rewardSettings: gameRewardSettingsSchema,
});
export const trackingPageTinyPackageGameBlockSchema: z.ZodType<TrackingPageTinyPackageGameBlock> =
  trackingPageTinyPackageGameBlockSchema_;
export interface TrackingPageTinyPackageGameBlock extends z.infer<
  typeof trackingPageTinyPackageGameBlockSchema_
> {}

// Discriminated union of all game block types
const trackingPageGameBlockSchema_ = z.discriminatedUnion("gameType", [
  trackingPageGalagaGameBlockSchema_,
  trackingPageCleanupCrewGameBlockSchema_,
  trackingPageDeliveryDashGameBlockSchema_,
  trackingPageFlappyFreightGameBlockSchema_,
  trackingPageTinyPackageGameBlockSchema_,
]);
export type TrackingPageGameBlock =
  | TrackingPageGalagaGameBlock
  | TrackingPageCleanupCrewGameBlock
  | TrackingPageDeliveryDashGameBlock
  | TrackingPageFlappyFreightGameBlock
  | TrackingPageTinyPackageGameBlock;
export const trackingPageGameBlockSchema: z.ZodType<TrackingPageGameBlock> =
  trackingPageGameBlockSchema_;

const trackingPageTextBlockSchema_ = baseTrackingPageBlockSchema_.extend({
  type: z.literal(TrackingPageBlockType.TEXT),
  html: z.string(),
  fontSize: z.number(),
  padding: z.number(),
  lineHeight: z.number(),
});
export const trackingPageTextBlockSchema: z.ZodType<TrackingPageTextBlock> =
  trackingPageTextBlockSchema_;
export interface TrackingPageTextBlock extends z.infer<
  typeof trackingPageTextBlockSchema_
> {}

/**
 * References a placement on the team's offer-network integration; resolved to
 * client-safe SDK config at render time. Empty id = unconfigured.
 */
const trackingPageOffersBlockSchema_ = baseTrackingPageBlockSchema_.extend({
  type: z.literal(TrackingPageBlockType.OFFERS),
  provider: createEnum(Object.values(OfferNetworkProvider)),
  // Default "" keeps blocks saved under the earlier inline-credential shape
  // (no offerPlacementId) parseable — an unconfigured block renders nothing.
  offerPlacementId: z.string().default(""),
});
export const trackingPageOffersBlockSchema: z.ZodType<TrackingPageOffersBlock> =
  trackingPageOffersBlockSchema_;
export interface TrackingPageOffersBlock extends z.infer<
  typeof trackingPageOffersBlockSchema_
> {}

const trackingPageBlockSchema_ = z.discriminatedUnion("type", [
  trackingPageHeaderBlockSchema_,
  trackingPageTrackingDetailsBlockSchema_,
  trackingPageItemsInFulfillmentBlockSchema_,
  trackingPageProductUpsellBlockSchema_,
  trackingPagePromotionBlockSchema_,
  trackingPageGameBlockSchema_,
  trackingPageSubscriptionUpsellBlockSchema_,
  trackingPageTrackingUpdatesBlockSchema_,
  trackingPageTextBlockSchema_,
  trackingPageOffersBlockSchema_,
]);
export const trackingPageBlockSchema: z.ZodType<TrackingPageBlock> =
  trackingPageBlockSchema_;
export type TrackingPageBlock =
  | TrackingPageHeaderBlock
  | TrackingPageTrackingDetailsBlock
  | TrackingPageItemsInFulfillmentBlock
  | TrackingPageProductUpsellBlock
  | TrackingPagePromotionBlock
  | TrackingPageGameBlock
  | TrackingPageSubscriptionUpsellBlock
  | TrackingPageTrackingUpdatesBlock
  | TrackingPageTextBlock
  | TrackingPageOffersBlock;

const orderLookupSettingsSchema_ = z.object({
  allowTrackingCodeOnlyAccess: z.boolean().default(false),
  backgroundColor: z.string(),
  backgroundImageUrl: z.string().nullish(),
  minPageHeightPx: z
    .number()
    .int()
    .min(0)
    .max(ABSOLUTE_MAX_PAGE_HEIGHT)
    .nullish(),
  cardBackgroundColor: z.string(),
  cardBorderRadius: z.number(),
  titleText: z.string(),
  titleColor: z.string(),
  titleFontFamily: z.string().nullish(),
  bodyFontFamily: z.string().nullish(),
  buttonText: z.string(),
  buttonOverride: enableableButtonOverrideSchema.nullish(),
  inputOverride: enableableInputOverrideSchema.nullish(),
  emailLabel: z.string().nullish(),
  emailPlaceholder: z.string().nullish(),
  orderLabel: z.string().nullish(),
  orderPlaceholder: z.string().nullish(),
});
export const orderLookupSettingsSchema: z.ZodType<OrderLookupSettings> =
  orderLookupSettingsSchema_;

export const DEFAULT_BADGE_SETTINGS = {
  backgroundColor: "#0f172a",
  borderColor: "#d7d7d7",
  fontColor: "#ffffff",
} as const;

const trackingPageSettingsSchema_ = z.object({
  name: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  blocks: z.array(trackingPageBlockSchema),
  background: z.object({
    backgroundColor: z.string(),
    backgroundImageUrl: z.string().nullish(),
    minPageHeightPx: z
      .number()
      .int()
      .min(0)
      .max(ABSOLUTE_MAX_PAGE_HEIGHT)
      .nullish(),
  }),
  typography: z.object({
    headerFontFamily: z.string(),
    headerFontColor: z.string(),
    headerFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    headerFontWeight: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    bodyFontFamily: z.string(),
    bodyFontColor: z.string(),
    bodyFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    bodyFontWeight: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    linkFontColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    deliveryDateFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
    trackingNumberUrlFontSize: z
      .number()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  }),
  blockSettings: z.object({
    backgroundColor: z.string(),
    borderRadius: z.number(),
    borderColor: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined)
      .optional(),
  }),
  badgeSettings: z
    .object({
      backgroundColor: z.string(),
      borderColor: z.string(),
      fontColor: z.string(),
    })
    .default(() => DEFAULT_BADGE_SETTINGS),
  buttonBorderRadius: z
    .number()
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  pageButtonSettings: pageButtonSettingsSchema
    .nullish()
    .transform(
      (v): PageButtonSettings => v ?? trackingPagePageButtonSettingsDefault(),
    ),
  shipmentSwitcherSettings: z
    .object({
      dropdownFontFamily: z.string().nullish(),
      descriptionFontFamily: z.string().nullish(),
      dropdownBackgroundColor: z
        .string()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      dropdownBorderColor: z
        .string()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      dropdownTextColor: z
        .string()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      descriptionTextColor: z
        .string()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      dropdownBorderRadius: z
        .number()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      showDropdown: z
        .boolean()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      showDescription: z
        .boolean()
        .nullish()
        .transform((v) => v ?? undefined)
        .optional(),
      descriptionText: z.string().nullish(),
    })
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
});

export const trackingPageSettingsSchema: z.ZodType<TrackingPageSettings> =
  trackingPageSettingsSchema_;
export interface TrackingPageSettings extends z.infer<
  typeof trackingPageSettingsSchema_
> {}

const trackingPageSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  settings: trackingPageSettingsSchema,
  isDefault: z.boolean().default(false),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const trackingPageSchema: z.ZodType<TrackingPage> = trackingPageSchema_;
export interface TrackingPage extends z.infer<typeof trackingPageSchema_> {}
